import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Calendar, Clock, ChevronRight, Share2, ArrowLeft, ExternalLink, Tag } from "lucide-react";
import { Button } from "@/components/ui/button";
import Footer from "@/components/Footer";
import SeoHead from "@/components/SeoHead";
import { getBlogArticle } from "@/lib/api";
import { toast } from "sonner";

const BlogPostPage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchArticle = async () => {
      setLoading(true);
      try {
        const data = await getBlogArticle(slug);
        setArticle(data);
      } catch (error) {
        console.error("Error fetching article:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchArticle();
    window.scrollTo(0, 0);
  }, [slug]);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try {
        await navigator.share({ title: article?.title, url });
      } catch (err) { /* cancelled */ }
    } else {
      navigator.clipboard.writeText(url);
      toast.success("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="h-6 w-48 bg-gray-200 animate-pulse rounded mb-6" />
          <div className="h-10 w-full bg-gray-200 animate-pulse rounded mb-4" />
          <div className="h-[350px] bg-gray-200 animate-pulse rounded-2xl mb-8" />
          <div className="space-y-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-4 w-full bg-gray-200 animate-pulse rounded" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">Article Not Found</h1>
          <Link to="/blog">
            <Button className="bg-[#0EA5E9] hover:bg-[#0284C7]">Back to Blog</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-amber-50" data-testid="blog-post-page">
      <SeoHead
        title={article.title}
        description={article.summary}
        path={`/blog/${article.slug}`}
        type="article"
        image={article.image}
      />
      {/* Article JSON-LD */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article.title,
        "description": article.summary,
        "image": article.image,
        "datePublished": article.date,
        "dateModified": article.date,
        "author": { "@type": "Organization", "name": "PublicBeachAccess.com" },
        "publisher": {
          "@type": "Organization",
          "name": "Digital Properties, LLC",
          "logo": { "@type": "ImageObject", "url": "https://publicbeachaccess.com/favicon-32x32.png" }
        },
        "mainEntityOfPage": { "@type": "WebPage", "@id": `https://publicbeachaccess.com/blog/${article.slug}` }
      })}} />

      {/* Article Header */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-10">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <Link to="/" className="hover:text-sky-600">Home</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <Link to="/blog" className="hover:text-sky-600">Blog</Link>
          <ChevronRight className="w-3.5 h-3.5" />
          <span className="text-gray-800 font-medium truncate max-w-[200px]">{article.title}</span>
        </nav>

        {/* Meta */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="inline-flex items-center gap-1 bg-sky-100 text-sky-700 text-xs font-semibold px-3 py-1 rounded-full">
            <Tag className="w-3 h-3" />
            {article.category}
          </span>
          {article.state && (
            <Link to={`/state/${article.state_slug}`} className="text-xs text-gray-500 hover:text-sky-600 font-medium">
              {article.state}
            </Link>
          )}
        </div>

        <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-gray-900 font-['Outfit'] tracking-tight leading-tight mb-4">
          {article.title}
        </h1>

        <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1.5">
              <Calendar className="w-4 h-4" />
              {new Date(article.date).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}
            </span>
            <span className="flex items-center gap-1.5">
              <Clock className="w-4 h-4" />
              {article.read_time}
            </span>
          </div>
          <Button variant="outline" size="sm" onClick={handleShare} className="border-gray-200">
            <Share2 className="w-4 h-4 mr-2" /> Share
          </Button>
        </div>

        {/* Hero Image */}
        <div className="rounded-2xl overflow-hidden shadow-lg mb-8">
          <img src={article.image} alt={article.title} className="w-full h-[220px] sm:h-[350px] object-cover" />
        </div>
      </div>

      {/* Article Content */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="prose prose-lg max-w-none">
          {article.content.map((block, i) => {
            if (block.type === "paragraph") {
              return <p key={i} className="text-gray-700 leading-relaxed mb-5 text-base sm:text-lg">{block.text}</p>;
            }
            if (block.type === "heading") {
              return <h2 key={i} className="text-xl sm:text-2xl font-bold text-gray-900 font-['Outfit'] mt-8 mb-4">{block.text}</h2>;
            }
            if (block.type === "list") {
              return (
                <ul key={i} className="mb-5 space-y-2 pl-1">
                  {block.items.map((item, j) => (
                    <li key={j} className="flex items-start gap-3 text-gray-700 text-base sm:text-lg">
                      <span className="w-2 h-2 rounded-full bg-sky-500 mt-2.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              );
            }
            return null;
          })}
        </div>

        {/* Source */}
        {article.source_url && (
          <div className="mt-8 pt-6 border-t border-gray-200">
            <a
              href={article.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-sky-600 hover:text-sky-700 font-medium"
            >
              <ExternalLink className="w-4 h-4" /> View Original Source
            </a>
          </div>
        )}

        {/* Related Beaches */}
        {article.related_beaches && article.related_beaches.length > 0 && (
          <div className="mt-10 pt-8 border-t border-gray-200">
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 font-['Outfit'] mb-2">
              Related Beaches {article.state ? `in ${article.state}` : "to Explore"}
            </h2>
            <p className="text-gray-500 text-sm mb-5">
              Discover top-rated public beach access points {article.state ? `in ${article.state}` : "across the US"}.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {article.related_beaches.map((beach) => (
                <Link
                  key={beach.slug}
                  to={`/beach/${beach.slug}`}
                  className="group block bg-white border border-gray-200 rounded-xl p-4 hover:shadow-lg hover:border-sky-200 transition-all"
                  data-testid={`related-beach-${beach.slug}`}
                >
                  <h3 className="font-semibold text-gray-900 group-hover:text-sky-600 transition-colors text-sm leading-snug mb-1.5 line-clamp-2">
                    {beach.name}
                  </h3>
                  <p className="text-xs text-gray-500 mb-2">{beach.city}, {beach.state}</p>
                  <div className="flex items-center gap-2">
                    {beach.average_rating && (
                      <span className="text-xs font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                        {beach.average_rating.toFixed(1)} stars
                      </span>
                    )}
                    {beach.has_parking && (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">Parking</span>
                    )}
                    {beach.has_bathrooms && (
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">Restrooms</span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {/* Back to Blog */}
        <div className="mt-8">
          <Link to="/blog" className="inline-flex items-center gap-2 text-gray-600 hover:text-sky-600 font-medium transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to all articles
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
